import { makeObservable, observable, action, computed } from 'mobx';
import { SectionsNodeDto, SectionSyncRuleDto, SubmissionScope } from '../../types';
import { NodeBaseModel, SectionModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';
import { SectionSyncManager } from '../../misc';

export default class SectionsNodeModel extends NodeBaseModel {
    sections: SectionModel[] = [];

    submissionScope: SubmissionScope | null;

    readonly sectionSyncRules: SectionSyncRuleDto[] | null;

    readonly sectionSyncManager: SectionSyncManager;

    constructor(data: SectionsNodeDto, sources: SourceModelBase[]) {
        super(data);

        this.sectionSyncRules = data.sectionSyncRules;
        this.submissionScope = data.submissionScope;
        this.sectionSyncManager = new SectionSyncManager(this.sectionSyncRules, () => this.sections);

        this.sections = data.sections.map(section => new SectionModel(this, section, sources));

        makeObservable(this, {
            sections: observable,
            submissionScope: observable,
            inputs: computed,
            update: action,
            setSubmissionScope: action
        });
    }

    refreshExpandedState() {
        this.sections.forEach(section => section.refreshExpandedState());
    }

    getSectionByInputGuid(guid: string) {
        return this.sections.find(section => section.inputs.some(input => input.guid === guid));
    }

    update(data: SectionsNodeDto, sources: SourceModelBase[]) {
        data.sections.forEach(section => {
            const existingSection = this.sections.find(s => s.id === section.id);

            if (!existingSection) {
                this.sections.push(new SectionModel(this, section, sources));
            } else {
                existingSection.update(section, sources);
            }
        });

        this.sections = this.sections.filter(section => data.sections.some(s => s.id === section.id));
    }

    getInputsAtSameLevel(inputGuid: string) {
        const section = this.getSectionByInputGuid(inputGuid);
        return section ? section.inputs : [];
    }

    setSubmissionScope(submissionScope: SubmissionScope) {
        this.submissionScope = submissionScope;
    }

    get inputs() {
        return this.sections.flatMap(section => section.inputs);
    }

    get dto(): SectionsNodeDto {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            sections: this.sections.map(section => section.dto),
            childNodes: this.childNodes.map(node => node.dto),
            sectionSyncRules: this.sectionSyncRules,
            submissionScope: this.submissionScope
        };
    }
}
